const escapeXml = (value) => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&apos;");

// Updates a value in the worksheet XML without touching its style attribute.
// This is what keeps the shared Excel template's original colour and borders.
export const replaceWorksheetCell = (worksheetXml, cellAddress, value, type = "string") => {
  const cellPattern = new RegExp(
    `<c([^>]*\\br="${cellAddress}"[^>]*?)(?:\\/>|>[\\s\\S]*?<\\/c>)`
  );

  return worksheetXml.replace(cellPattern, (_match, attributes) => {
    const cleanAttributes = attributes.replace(/\s+t="[^"]*"/g, "");
    if (type === "number") {
      return `<c${cleanAttributes}><v>${Number(value || 0)}</v></c>`;
    }
    return `<c${cleanAttributes} t="inlineStr"><is><t>${escapeXml(value)}</t></is></c>`;
  });
};

// Fill the original colored workbook without recreating its cells or styles.
export const populateContainerWorksheet = (worksheetXml, containers) => {
  if (containers.length > 10) throw new Error("The fixation template supports up to 10 containers.");
  let xml = worksheetXml;
  ["B5", "B6", "E6", "B7", "E7", "B8", "E8", "B9", "E9", "B12", "B13", "B14", "B15", "D35", "E35", "F35"].forEach((address) => {
    xml = replaceWorksheetCell(xml, address, "");
  });
  for (let row = 19; row <= 22; row += 1) {
    for (const column of "ABCDEFG") xml = replaceWorksheetCell(xml, `${column}${row}`, "");
  }
  for (let index = 0; index < 10; index += 1) {
    const row = index + 25;
    const container = containers[index];
    xml = replaceWorksheetCell(xml, `B${row}`, container?.containerNo || "");
    xml = replaceWorksheetCell(xml, `C${row}`, container?.netWeight ?? "", container ? "number" : "string");
  }
  const total = containers.reduce((sum, container) => sum + Number(container.netWeight || 0), 0);
  return replaceWorksheetCell(xml, "C35", Number(total.toFixed(3)), "number");
};

// Populate the current deal while retaining the workbook styles.
export const populateDealWorksheet = (worksheetXml, { deal = {}, fixations = [], materials = [], noticeDate = "" }) => {
  let xml = worksheetXml;
  const unique = (values) => [...new Set(values.filter(value => value !== "" && value !== null && value !== undefined))].join(" / ");
  const date = (value) => String(value || "").split("T")[0];
  const types = { LME_LOWEST: "LME Lowest", LME_HIGHEST: "LME Highest", LME_3_MONTH: "LME 3 Month", LME_3MONTH: "LME 3 Month", LME_CASH: "LME Cash", AVERAGE: "Average", LME_AVERAGE: "Average" };
  const values = {
    B5: deal.DealNo || deal.DealID || "",
    B6: unique(fixations.map(row => row.FixationID ?? row.Fixation)), E6: noticeDate,
    B7: deal.ShipperName || "", E7: deal.BuyerName || "",
    B8: unique(materials.map(row => row.commodity)), E8: unique(materials.map(row => row.name)),
    B9: deal.SCRNo || "", E9: deal.PONo || "",
    B12: unique(fixations.map(row => date(row.FixationDate))),
    B13: unique(fixations.map(row => row.FixationValue ?? row.LMEPrice ?? "")),
    B14: unique(fixations.map(row => types[row.FixationOption] || row.FixationOption || row.FixationType))
  };
  for (const [address, value] of Object.entries(values)) xml = replaceWorksheetCell(xml, address, value);
  xml = replaceWorksheetCell(xml, "B15", fixations.reduce((sum, row) => sum + Number(row.Quantity || 0), 0), "number");

  // Extend the material table using its styled first row, moving later sections down.
  const extraRows = Math.max(0, materials.length - 4);
  if (extraRows) {
    xml = xml.replace(/\b(r|ref|sqref)="([^"]+)"/g, (_match, attribute, value) => {
      const shifted = value.replace(/([A-Z]*)(\d+)/g, (ref, column, row) => Number(row) >= 23 ? `${column}${Number(row) + extraRows}` : ref);
      return `${attribute}="${shifted}"`;
    });
  }
  const templateRow = xml.match(/<row\b[^>]*\br="19"[^>]*>[\s\S]*?<\/row>/)?.[0];
  if (!templateRow) throw new Error("Material row missing from fixation template");
  const materialRows = materials.map((material, index) => {
    const row = index + 19;
    let rowXml = templateRow.replace(/\br="([A-Z]*)19"/g, (_match, column) => `r="${column}${row}"`);
    const entries = [index + 1, material.name, material.lme, material.percentage === "" ? "" : Number(material.percentage) / 100, material.adjustment, material.price];
    entries.forEach((value, column) => {
      rowXml = replaceWorksheetCell(rowXml, `${String.fromCharCode(65 + column)}${row}`, value ?? "", column === 1 || value === "" || value == null ? "string" : "number");
    });
    return rowXml;
  }).join("");
  if (materials.length) {
    for (let row = 20; row <= Math.min(22, materials.length + 18); row += 1) {
      xml = xml.replace(new RegExp(`<row\\b[^>]*\\br="${row}"[^>]*>[\\s\\S]*?<\\/row>`), "");
    }
    xml = xml.replace(/<row\b[^>]*\br="19"[^>]*>[\s\S]*?<\/row>/, () => materialRows);
  }
  return { xml, extraRows };
};

export const removeCalculationChain = async (workbookZip) => {
  workbookZip.remove("xl/calcChain.xml");
  const references = [
    ["xl/_rels/workbook.xml.rels", /<Relationship\b[^>]*\bType="[^"]*\/calcChain"[^>]*\/>/g],
    ["[Content_Types].xml", /<Override\b[^>]*\bPartName="\/xl\/calcChain\.xml"[^>]*\/>/g]
  ];
  for (const [fileName, pattern] of references) {
    const file = workbookZip.file(fileName);
    if (file) workbookZip.file(fileName, (await file.async("string")).replace(pattern, ""));
  }
};

